import type { FieldLevel, FieldSection } from '@numistic/contracts'
import { fieldsForType, type ResolvedField } from '@numistic/domain'
import { useMeta } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { useMemo } from 'react'

import { displayValue } from './display.js'
import { useRefResolver, type RefResolverOptions } from './ref-resolver.js'

/** Карточка «только чтение» по тем же метаданным, что и форма. Пустые поля не показываются. */
export function MetaView({
  typeCode,
  level,
  values,
  sources,
  hide = [],
  refs,
  grouped = false,
  sectionOrder,
}: {
  typeCode: string
  level: FieldLevel
  values: Record<string, unknown>
  /** Поля, уже показанные рядом (например, название в заголовке страницы). */
  hide?: string[]
  /** Для экземпляра: откуда значение — «свои» переопределения помечаются. */
  sources?: Record<string, 'catalog' | 'variant' | 'own'>
  /** Названия своих коллекций/мест хранения и разновидностей (см. useRefResolver). */
  refs?: RefResolverOptions
  /** Разбить поля по разделам формы с подзаголовками (для длинных карточек). */
  grouped?: boolean
  /** Порядок разделов при группировке (например, у экземпляра — сначала «Экземпляр»). */
  sectionOrder?: FieldSection[]
}) {
  const { t } = useTranslation()
  const meta = useMeta().data
  const fields = useMemo(
    () => (meta ? fieldsForType(meta, typeCode, level) : []),
    [meta, typeCode, level],
  )
  const kinds = useMemo(() => new Set(fields.map((f) => f.def.refKind ?? '')), [fields])
  const resolve = useRefResolver(meta, kinds, refs)
  if (!meta) return null
  const codes = new Set(fields.map((f) => f.def.code))
  const rows = fields
    .filter((f) => !hide.includes(f.def.code))
    .map((f) => {
      const text = displayValue(meta, f.def, values[f.def.code], resolve)
      // Пара «x_value» + «x_unit» (номинал и его единица) показывается одной строкой.
      const pair = f.def.code.endsWith('_value') ? f.def.code.replace(/_value$/, '_unit') : null
      if (pair && codes.has(pair) && text) {
        const unitDef = fields.find((x) => x.def.code === pair)!.def
        const unit = displayValue(meta, unitDef, values[pair], resolve)
        return { f, text: unit ? `${text} ${unit}` : text, label: f.def.name.ru }
      }
      if (f.def.code.endsWith('_unit') && codes.has(f.def.code.replace(/_unit$/, '_value'))) {
        const value = values[f.def.code.replace(/_unit$/, '_value')]
        if (value !== undefined && value !== null && value !== '') return { f, text: '', label: '' }
      }
      return { f, text, label: f.def.name.ru }
    })
    .filter((r) => r.text !== '')
  if (!rows.length) return <p className="text-sm text-muted-foreground">{t('meta.noData')}</p>

  const list = (items: typeof rows) => (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[minmax(10rem,auto)_1fr]">
      {items.map(({ f, text, label }) => {
        const own =
          sources?.[f.def.code] === 'own' && f.override ? (
            <span className="ml-2 rounded bg-primary/15 px-1.5 text-xs text-primary">
              {t('specimen.own')}
            </span>
          ) : null
        // Длинный текст (описание сторон, заметки) — на всю ширину, подпись сверху.
        return WIDE.has(f.def.dataType) ? (
          <div key={f.def.code} className="grid gap-1 sm:col-span-2">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="text-sm leading-relaxed whitespace-pre-line">
              {text}
              {own}
            </dd>
          </div>
        ) : (
          <div key={f.def.code} className="contents">
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className="text-sm whitespace-pre-line">
              {text}
              {own}
            </dd>
          </div>
        )
      })}
    </dl>
  )

  if (!grouped) return list(rows)
  const groups = new Map<FieldSection, typeof rows>()
  for (const row of rows) groups.set(row.f.section, [...(groups.get(row.f.section) ?? []), row])
  const order = [...new Set([...(sectionOrder ?? []), ...groups.keys()])].filter((x) =>
    groups.has(x),
  )
  if (order.length < 2) return list(rows)
  return (
    <div className="grid gap-5">
      {order.map((section) => (
        <section key={section} className="grid gap-2 border-t pt-4 first:border-t-0 first:pt-0">
          <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t(`sections.${section}`)}
          </h3>
          {list(groups.get(section)!)}
        </section>
      ))}
    </div>
  )
}

const WIDE = new Set<ResolvedField['def']['dataType']>(['longtext'])
